import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { useFitbitStatus } from '../hooks/useFitbitStatus';
import ActivityChart from '../components/fitbit/ActivityChart';
import SleepDataView from '../components/fitbit/SleepDataView';
import HeartRateChart from '../components/fitbit/HeartRateChart';
import WeightChart from '../components/fitbit/WeightChart';
import SpO2Chart from '../components/fitbit/SpO2Chart';
import TemperatureChart from '../components/fitbit/TemperatureChart';
import NutritionDataView from '../components/data/NutritionDataView';
import HydrationDataView from '../components/data/HydrationDataView';
import SupplementsDataView from '../components/data/SupplementsDataView';
import FitnessDataView from '../components/data/FitnessDataView';
import MitoDataView from '../components/data/MitoDataView';
import LogHistory from '../components/data/LogHistory';

// History leads: it's the record of what YOU logged, in your words, while every other tab
// is a per-metric chart of one measurement. It moved here from a second tab on the Log
// page — reviewing what happened belongs on the review page, and it bought the capture
// screen back a permanent strip of phone real estate.
const TABS = [
  { key: 'history', label: 'History', fitbit: false },
  { key: 'activity', label: 'Activity', fitbit: true },
  { key: 'workouts', label: 'Workouts', fitbit: false },
  { key: 'sleep', label: 'Sleep', fitbit: true },
  { key: 'heart-rate', label: 'Heart Rate', fitbit: true },
  { key: 'weight', label: 'Weight', fitbit: false },
  { key: 'spo2', label: 'SpO2', fitbit: true },
  { key: 'temperature', label: 'Temperature', fitbit: true },
  { key: 'mito', label: 'Mito Index', fitbit: false },
  { key: 'nutrition', label: 'Nutrition', fitbit: false },
  { key: 'supplements', label: 'Supplements', fitbit: false },
  { key: 'hydration', label: 'Hydration', fitbit: false },
];

const DataPage = () => {
  const { fitbitStatus, loading: loadingStatus } = useFitbitStatus();
  const [activeTab, setActiveTab] = useState('history');

  const currentTab = TABS.find((t) => t.key === activeTab);
  const isFitbitTab = currentTab?.fitbit;
  const showFitbitWarning = isFitbitTab && !loadingStatus && !fitbitStatus.connected;

  const renderTabContent = () => {
    if (showFitbitWarning) {
      return (
        <Alert variant="default">
          <AlertDescription>
            Your Fitbit account is not connected. Connect it in your{' '}
            <Link to="/me" className="text-primary-600 hover:underline">
              profile settings
            </Link>{' '}
            to view {currentTab.label.toLowerCase()} data.
          </AlertDescription>
        </Alert>
      );
    }

    switch (activeTab) {
      case 'history':
        return <LogHistory />;
      case 'activity':
        return <ActivityChart />;
      case 'workouts':
        return <FitnessDataView />;
      case 'sleep':
        return <SleepDataView />;
      case 'heart-rate':
        return <HeartRateChart />;
      case 'weight':
        return <WeightChart />;
      case 'spo2':
        return <SpO2Chart />;
      case 'temperature':
        return <TemperatureChart />;
      case 'mito':
        return <MitoDataView />;
      case 'nutrition':
        return <NutritionDataView />;
      case 'supplements':
        return <SupplementsDataView />;
      case 'hydration':
        return <HydrationDataView />;
      default:
        return <ActivityChart />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-10">
      {/* Tab Bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Loading state for Fitbit status check */}
      {loadingStatus && isFitbitTab ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};

export default DataPage;
